// src/components/Warehouses/index.js

import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast"; // Import react-hot-toast
import {
  FaPlus,
  FaPen,
  FaTrash,
  FaChevronDown,
  FaMapMarkerAlt,
  FaBoxOpen,
} from "react-icons/fa";
import "./style.css";

// --- Delete Confirmation Modal Component ---
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="modal-container">
      <div className="modal-box">
        <h2 className="modal-title">{t("Confirm Deletion")}</h2>
        <p className="modal-confirmation-text">
          {t("Are you sure you want to delete")} <strong>"{itemName}"</strong>?{" "}
          {t("This action cannot be undone.")}
        </p>
        <div className="modal-buttons">
          <button type="button" className="button-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="button" className="button-danger" onClick={onConfirm}>
            {t("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Warehouses Component ---
const Warehouses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);

  // Data state
  const [warehouses, setWarehouses] = useState([]);
  const [actualProductCounts, setActualProductCounts] = useState({});

  // Modal state for Create/Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentWarehouseId, setCurrentWarehouseId] = useState(null);
  const [name, setName] = useState("");
  const [vertices, setVertices] = useState("");
  const [isMain, setIsMain] = useState(false);

  // NEW: State for Delete Confirmation Modal
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Filter and UI state
  const [filterOption, setFilterOption] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const farmId = userData.Employee?.farmId;
  const tenantId = userData.Employee?.tenantId;

  // Fetch the actual product count for each warehouse
  const fetchActualProductCounts = useCallback(
    async (warehouseList) => {
      const counts = {};

      // Fetch counts in parallel for better performance
      const countPromises = warehouseList.map(async (warehouse) => {
        try {
          // First get base products
          const response = await axios.get(
            `api/inventoryItemDetails/findByLocationId/${warehouse.id}`,
            { headers: { Authorization: `${token}` } }
          );

          let totalCount =
            response.data.totalCount ||
            (response.data.items ? response.data.items.length : 0);

          // Also try to get allocated products (transferred items)
          try {
            const allocResponse = await axios.get(
              `api/inventoryItemsAllocation/location/${warehouse.id}`,
              { headers: { Authorization: `${token}` } }
            );

            if (allocResponse.data && allocResponse.data.items) {
              const allocatedProducts = allocResponse.data.items || [];
              const baseProductIds = new Set(
                response.data.items?.map((p) => p.id) || []
              );

              // Count additional transferred products not in base list
              const additionalCount = allocatedProducts.filter(
                (p) => !baseProductIds.has(p.id)
              ).length;
              totalCount += additionalCount;
            }
          } catch (allocErr) {
            // If allocation endpoint fails, just use base count
            console.log(
              `Allocation endpoint not accessible for warehouse ${warehouse.id}`
            );
          }

          counts[warehouse.id] = totalCount;
        } catch (error) {
          console.error(
            `Error fetching products for warehouse ${warehouse.id}:`,
            error
          );
          // Fallback to the original count if fetch fails
          counts[warehouse.id] = warehouse.numOfItems || 0;
        }
      });

      await Promise.all(countPromises);
      setActualProductCounts(counts);
    },
    [token]
  );

  // Fetch warehouses function
  const fetchWarehouses = useCallback(async () => {
    if (!farmId) return;
    try {
      const response = await axios.get(
        `api/inventoryLocation/findByFarmId/${farmId}`,
        { headers: { Authorization: `${token}` } }
      );
      const warehouseList = response.data.items || [];
      setWarehouses(warehouseList);

      // Fetch actual product counts for each warehouse
      fetchActualProductCounts(warehouseList);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  }, [farmId, token, fetchActualProductCounts]);

  // --- Data Fetching and Effects ---
  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // Refresh when component regains focus or returns from navigation
  useEffect(() => {
    const handleFocus = () => {
      // Re-fetch everything to get updated counts
      fetchWarehouses();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchWarehouses]);

  // Handle navigation state for refresh
  useEffect(() => {
    if (location.state?.refresh) {
      fetchWarehouses();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchWarehouses, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Navigation Handlers ---
  const handleWarehouseClick = (warehouseId) => {
    navigate(`/inventory/warehouses/${warehouseId}/products`, {
      state: { warehouseId },
    });
  };

  const handleAddProductClick = (warehouse) => {
    navigate("/add-product", { 
      state: { 
        checkboxChecked: true,
        warehouseId: warehouse.id,
        warehouseName: getWarehouseDisplayName(warehouse),
        fromWarehouse: true
      } 
    });
  };

  // --- Create/Edit Modal Handlers ---
  const resetModalState = () => {
    setCurrentWarehouseId(null);
    setName("");
    setVertices("");
    setIsMain(false);
  };

  const handleCreateWarehouseClick = () => {
    resetModalState();
    setIsModalOpen(true);
  };

  const handleEditWarehouseClick = (warehouse) => {
    setCurrentWarehouseId(warehouse.id);
    const currentName =
      i18n.language === "ar" ? warehouse.nameAr : warehouse.nameEn;
    setName(currentName || warehouse.nameAr || warehouse.nameEn);
    setVertices(warehouse.vertices || "");
    setIsMain(warehouse.isMainLocation || false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = async () => {
    if (!name) {
      toast.error(t("Name is required"));
      return;
    }
    const requestBody = {
      id: currentWarehouseId,
      nameAr: name,
      nameEn: name,
      vertices: vertices,
      farmId,
      tenantId,
      isMainLocation: isMain,
    };

    const promise = new Promise(async (resolve, reject) => {
      try {
        let updatedWarehouses = [...warehouses];
        if (currentWarehouseId) {
          await axios.put(`api/inventoryLocation`, requestBody, {
            headers: { Authorization: `${token}` },
          });
          updatedWarehouses = warehouses.map((warehouse) =>
            warehouse.id === currentWarehouseId
              ? { ...warehouse, ...requestBody }
              : warehouse
          );
          resolve(t("Warehouse updated successfully!"));
        } else {
          const response = await axios.post(
            `api/inventoryLocation`,
            requestBody,
            {
              headers: { Authorization: `${token}` },
            }
          );
          const newWarehouseData = response.data.items[0];
          const newWarehouse = {
            ...requestBody,
            id: newWarehouseData.id,
            numOfItems: 0,
            ...newWarehouseData,
          };
          updatedWarehouses.push(newWarehouse);
          resolve(t("Warehouse created successfully!"));
        }
        setWarehouses(updatedWarehouses);
        handleCloseModal();
        // Refresh product counts after save
        fetchActualProductCounts(updatedWarehouses);
        
        // If this was a new warehouse creation, add a small delay
        // to allow backend propagation before navigation
        if (!currentWarehouseId) {
          setTimeout(() => {
            // Force a refresh when navigating to other inventory pages
            window.dispatchEvent(new Event('warehouseCreated'));
          }, 500);
        }
      } catch (error) {
        console.error("Error saving warehouse:", error);
        reject(t("Failed to save warehouse."));
      }
    });

    toast.promise(promise, {
      loading: t("Saving..."),
      success: (message) => message,
      error: (message) => message,
    });
  };

  // --- Delete Modal Handlers ---
  const openDeleteConfirmationModal = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setDeleteModalOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setDeleteModalOpen(false);
    setSelectedWarehouse(null);
  };

  const handleDeleteWarehouse = async () => {
    if (!selectedWarehouse) return;

    try {
      await axios.delete(`api/inventoryLocation/${selectedWarehouse.id}`, {
        headers: { Authorization: `${token}` },
      });
      const updatedWarehouses = warehouses.filter(
        (warehouse) => warehouse.id !== selectedWarehouse.id
      );
      setWarehouses(updatedWarehouses);
      toast.success(
        `${t("Warehouse")} "${getWarehouseDisplayName(selectedWarehouse)}" ${t(
          "deleted successfully."
        )}`
      );
      closeDeleteConfirmationModal();
      // Refresh product counts after delete
      fetchActualProductCounts(updatedWarehouses);
    } catch (error) {
      console.error("Error deleting warehouse:", error);
      toast.error(t("Failed to delete warehouse."));
    }
  };

  // Helper function to get display name for notifications and modal
  const getWarehouseDisplayName = (warehouse) => {
    if (!warehouse) return "";
    return i18n.language === "ar"
      ? warehouse.nameAr
      : warehouse.nameEn || warehouse.nameAr;
  };

  // --- Filtering Logic ---
  const filteredWarehouses = warehouses.filter((warehouse) => {
    if (filterOption === "all") return true;
    if (filterOption === "main") return warehouse.isMainLocation;
    if (filterOption === "secondary") return !warehouse.isMainLocation;
    return true;
  });

  const filterOptionsList = [
    { key: "all", label: t("All") },
    { key: "main", label: t("Main") },
    { key: "secondary", label: t("Secondary") },
  ];

  return (
    <div className="warehouses-page-container">
      {/* START: Toolbar for actions and filters */}
      <div className="content-toolbar">
        <div className="filter-dropdown-wrapper" ref={filterRef}>
          <button
            className="filter-dropdown-button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            {t("filter by")}:{" "}
            <span>
              {filterOptionsList.find((opt) => opt.key === filterOption)?.label}
            </span>
            <FaChevronDown
              className={`chevron-icon ${isFilterOpen ? "open" : ""}`}
            />
          </button>
          {isFilterOpen && (
            <div className="filter-dropdown-menu">
              {filterOptionsList.map((option) => (
                <div
                  key={option.key}
                  className="filter-dropdown-item"
                  onClick={() => {
                    setFilterOption(option.key);
                    setIsFilterOpen(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="button-primary" onClick={handleCreateWarehouseClick}>
          <FaPlus /> {t("Create Warehouse")}
        </button>{" "}
      </div>
      {/* END: Toolbar */}

      {/* START: Warehouse List Grid */}
      <div className="warehouses-list-grid">
        {filteredWarehouses.length > 0 ? (
          filteredWarehouses.map((warehouse) => {
            const displayName = getWarehouseDisplayName(warehouse);
            const locationText = warehouse.vertices;

            return (
              <div
                className="warehouse-card"
                key={warehouse.id}
                onClick={() => handleWarehouseClick(warehouse.id)}
              >
                <div className="warehouse-card-header">
                  <div className="warehouse-title-group">
                    <h3 className="warehouse-name" title={displayName}>
                      {displayName}
                    </h3>
                    {locationText && locationText.trim() !== "" && (
                      <div className="warehouse-location">
                        <FaMapMarkerAlt />
                        <span>{locationText}</span>
                      </div>
                    )}
                  </div>
                  <div className="warehouse-card-menu-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditWarehouseClick(warehouse);
                      }}
                      aria-label={t("Edit")}
                    >
                      <FaPen />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteConfirmationModal(warehouse); // MODIFIED: Opens modal instead of direct delete
                      }}
                      aria-label={t("Delete")}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                <div className="warehouse-card-body">
                  <div className="warehouse-info-bar">
                    <div className="warehouse-info-item">
                      <FaBoxOpen />
                      <span>
                        {actualProductCounts[warehouse.id] !== undefined
                          ? actualProductCounts[warehouse.id]
                          : warehouse.numOfItems || 0}{" "}
                        {t("Items")}
                      </span>
                    </div>
                    <span
                      className={`warehouse-type-tag type-${
                        warehouse.isMainLocation ? "main" : "secondary"
                      }`}
                    >
                      {warehouse.isMainLocation ? t("Main") : t("Secondary")}
                    </span>
                  </div>
                </div>

                <div className="warehouse-card-footer">
                  <button
                    className="add-product-cta-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddProductClick(warehouse);
                    }}
                  >
                    <FaPlus /> {t("Add Product")}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-data-message">{t("No warehouses found.")}</div>
        )}
      </div>
      {/* END: Warehouse List Grid */}

      {/* START: Modals */}
      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-container">
          <div className="modal-box">
            <h2 className="modal-title">
              {currentWarehouseId
                ? t("Edit Warehouse")
                : t("Create New Warehouse")}
            </h2>
            <div className="modal-input">
              <label htmlFor="warehouseName">{t("Name")}*</label>
              <input
                id="warehouseName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="modal-input">
              <label htmlFor="warehouseVertices">
                {t("Location Description")}*
              </label>
              <input
                id="warehouseVertices"
                type="text"
                value={vertices}
                onChange={(e) => setVertices(e.target.value)}
              />
            </div>
            <div className="modal-checkbox-group">
              <input
                id="isMainWarehouse"
                type="checkbox"
                checked={isMain}
                onChange={(e) => setIsMain(e.target.checked)}
              />
              <label htmlFor="isMainWarehouse">{t("Main Warehouse")}</label>
            </div>
            <div className="modal-buttons">
              <button className="button-secondary" onClick={handleCloseModal}>
                {t("Cancel")}
              </button>
              <button className="button-primary" onClick={handleSave}>
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteConfirmationModal}
        onConfirm={handleDeleteWarehouse}
        itemName={getWarehouseDisplayName(selectedWarehouse)}
      />
      {/* END: Modals */}
    </div>
  );
};

export default Warehouses;
